import React from 'react';
import './NocMetricCardOverlay.css';

import Highcharts from 'highcharts';
import HighchartsReact, {
  HighchartsReactRefObject
} from 'highcharts-react-official';
import { useAppDispatch } from '../../../rmsReduxStore/rmsReduxHooks';
import { setRedirectedStateForAcChargerFilters } from '../../rmsReduxStore/acChargersRedux/acChargerCreators';
import { setChargerIdsForRedirection } from '../../rmsReduxStore/acNocDashboardRedux/acNocDashboardCreator';
import { setRedirectedStateForAcAlarmFilters } from '../../rmsReduxStore/acAlarmsRedux/acAlarmCreators';

interface DonutSlice {
  name: string;
  value: number;
  color?: string;
}

interface DonutChartProps {
  data: DonutSlice[];
  centerText: string;
  donutTitle: string;
  allChargerIds?: string[];
}

const AcDonutChart: React.FC<DonutChartProps> = ({ data, centerText, donutTitle, allChargerIds }) => {
  const chartRef = React.useRef<HighchartsReactRefObject>(null);
  const dispatch = useAppDispatch();
  const options: Highcharts.Options = {
    chart: {
      type: 'pie',
      height: 100,
      width: 180,
      backgroundColor: 'transparent'
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: false },

    subtitle: {
      useHTML: true,
      text: `
    <div style='
      display:flex;
      flex-direction:column;
      justify-content:center;
      align-items:center;
      width:80px;
      text-align:center;
      pointer-events:none;
    '>
      <div style='font-size:8px;font-weight:500;margin-bottom:2px;color:rgba(77,77,77,1)'>
        ${donutTitle}
      </div>
      <div style='font-size:12px;font-weight:700;color:rgba(62,62,62,1)'>
        ${centerText}
      </div>
    </div>
  `,
      verticalAlign: 'middle',
      y: 12,
      x: -50
    },

    legend: {
      enabled: true,
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle',
       x: -20, // ← reduce space between donut and legend
      itemStyle: { color: '#000', fontSize: '12px' },
      symbolHeight: 10,
      symbolWidth: 10,
      symbolRadius: 50
    },
    
    plotOptions: {
      pie: {
        innerSize: '75%',
        size: '200%',
        
        borderWidth: 0,
        borderColor: 'transparent',
        dataLabels: { enabled: false },
        showInLegend: true,
        enableMouseTracking: true,
        stickyTracking: true,
        point: {
          events: {
            click: function () {
              if (donutTitle === 'Online') {
                setRedirectedStateForAcChargerFilters(true, this.name, null, null)(dispatch)
                  .then((res) => {
                    if (res === 'Action dispatched successfully') {
                      dispatch(setChargerIdsForRedirection(allChargerIds))
                      window.open('/ac/manage-chargers?redirectedFrom=nocDashboard', '_blank');
                    }
                  })
                  .catch((error) => {
                    console.error('Error dispatching action:', error);
                  });
              }
              else if (donutTitle === 'Alarms') {
                setRedirectedStateForAcAlarmFilters(true, this.name, '', '', '', '', [], allChargerIds)(dispatch)
                  .then((res) => {
                    if (res === 'Action dispatched successfully') {
                      window.open('/ac/alarms?redirectedFrom=nocDashboard', '_blank');
                    }
                  })
                  .catch((error) => {
                    console.error('Error dispatching action:', error); 
                  });
              }

            }

            ,

            mouseOver() {
              this.series.chart.setSubtitle({ text: '' });
            },

            mouseOut() {
              this.series.chart.setSubtitle({
                text: `
                <div style='
                  display:flex;
                  flex-direction:column;
                  justify-content:center;
                  align-items:center;
                  width:80px;
                  text-align:center;
                  pointer-events:none;
                '>
                  <div style='font-size:9px;margin-bottom:2px;'>${donutTitle}</div>
                  <div style='font-size:12px;font-weight:600;'>${centerText}</div>
                </div>
                `,
                useHTML: true,            
              });
            }
          }
        }
      }
    },

    tooltip: {
      useHTML: true,
      borderWidth: 0,
      backgroundColor: 'transparent',
      shadow: false,
      padding: 0,

      formatter() {
        return `
        <div style="
          background:white;
          padding:6px 10px;
          border-radius:4px;
          box-shadow:0px 2px 6px rgba(0,0,0,0.15);
          font-size:13px;
          border:1px solid #e5e5e5;
          text-align:center;
        ">
          <div><b>${this.point.name}</b></div>
          <div>${this.point.y}</div>
        </div>
        `;
      },

      positioner(labelWidth, labelHeight, point) {
        return { x: point.plotX + 15, y: point.plotY + 15 };
      }
    },

    series: [
      {
        type: 'pie',
        tooltip: { followPointer: true },
        data: data.map(item => ({
          name: item.name,
          y: item.value,
          color: item.color
        }))
      }
    ]
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      chartRef.current?.chart?.reflow();
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      ref={chartRef}
    />
  );
};

export default AcDonutChart;
